import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { ActivationService } from '../../../../services/activation.service';
import { PERMISSIONS } from '../../../../cores/permissions';
import { DisableWithoutPermissionDirective } from '../../../../cores/permissions/disable-without-permission.directive';
import { PermissionService } from '../../../../cores/permissions/permission.service';

import { resetCartStates } from '../../../../stores/cart';
import { resetCouponStates } from '../../../../stores/coupon';
import { resetCustomerStates } from '../../../../stores/customer';
import { resetEmployeeStates } from '../../../../stores/employee';
import { resetGlobalRulesStates } from '../../../../stores/global';
import { resetHeldOrderStates } from '../../../../stores/order';
import { resetProductStates } from '../../../../stores/product';
import { resetPaymentStates } from '../../../../stores/payment';
import { resetLoyaltyStates } from '../../../../stores/loyalty';
import { ApiErrorService, NormalizedApiError } from '../../../../cores/errors/api-error.service';

const DEACTIVATION_DIALOG_BASE_Z_INDEX = 11000;
const DEACTIVATION_TOAST_BASE_Z_INDEX = 12000;
const DEACTIVATION_UNSYNCED_DATA_ERROR_CODE = 'DEVICE_DEACTIVATION_UNSYNCED_DATA';
const DEACTIVATION_UNSYNCED_ORDER_TYPE = 'order';

interface DeactivationUnsyncedItem {
  type: string;
  count: number;
}

interface DeactivationUnsyncedDisplayItem {
  typeLabel: string;
  count: number;
}

@Component({
  selector: 'app-deactivation-modal',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule, ToastModule, DisableWithoutPermissionDirective, TranslateModule],
  providers: [MessageService],
  templateUrl: './deactivation-modal.component.html',
})
export class DeactivationModalComponent {
  readonly deactivationDialogBaseZIndex = DEACTIVATION_DIALOG_BASE_Z_INDEX;
  readonly deactivationToastBaseZIndex = DEACTIVATION_TOAST_BASE_Z_INDEX;
  readonly deactivatePermission = PERMISSIONS.DEVICE_ACTIVATION.DEACTIVATE;

  visible = false;
  isLoading = false;
  deactivationErrorMessage: string | null = null;
  unsyncedItems: DeactivationUnsyncedDisplayItem[] = [];

  private activationService = inject(ActivationService);
  private messageService = inject(MessageService);
  private store = inject(Store);
  private permissionService = inject(PermissionService);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private apiErrorService = inject(ApiErrorService);

  show() {
    this.clearDeactivationError();
    this.visible = true;
  }

  hide() {
    this.clearDeactivationError();
    this.visible = false;
  }

  onDeactivate() {
    this.clearDeactivationError();

    if (!this.permissionService.hasPermission(this.deactivatePermission)) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('deactivation.no_permission_summary'),
        detail: this.translate.instant('deactivation.permission_denied'),
      });
      return;
    }

    this.isLoading = true;
    this.activationService.clearActivation().subscribe({
      next: () => {
        this.clearLocalState();
        this.isLoading = false;
        this.hide();
        this.router.navigate(['/env-config']);
      },
      error: (err) => {
        this.isLoading = false;
        const normalized = this.getDeactivateError(err);
        this.setDeactivationError(normalized);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('deactivation.error_summary'),
          detail: normalized.userMessage,
        });
      }
    });
  }

  private getDeactivateError(err: unknown): NormalizedApiError {
    return this.apiErrorService.normalizeApiError(
      err,
      'deactivation.error_fallback',
    );
  }

  private setDeactivationError(error: NormalizedApiError): void {
    if (error.errorCode !== DEACTIVATION_UNSYNCED_DATA_ERROR_CODE) {
      return;
    }

    const unsyncedItems = this.toUnsyncedDisplayItems(error.data);
    if (!unsyncedItems.length) {
      return;
    }

    this.deactivationErrorMessage = error.userMessage;
    this.unsyncedItems = unsyncedItems;
  }

  private toUnsyncedDisplayItems(data: unknown): DeactivationUnsyncedDisplayItem[] {
    if (!this.isRecord(data) || !Array.isArray(data['unsynced'])) {
      return [];
    }

    return data['unsynced']
      .map((item) => this.toUnsyncedItem(item))
      .filter((item): item is DeactivationUnsyncedItem => item !== null)
      .filter((item) => item.type === DEACTIVATION_UNSYNCED_ORDER_TYPE)
      .map((item) => ({
        typeLabel: this.translateUnsyncedType(item.type),
        count: item.count,
      }));
  }

  private toUnsyncedItem(item: unknown): DeactivationUnsyncedItem | null {
    if (!this.isRecord(item)) {
      return null;
    }

    const type = item['type'];
    const count = item['count'];
    if (typeof type !== 'string' || !type.trim() || typeof count !== 'number' || count <= 0) {
      return null;
    }

    return {
      type: type.trim(),
      count,
    };
  }

  private translateUnsyncedType(type: string): string {
    const translationKey = `deactivation.unsynced_type_${type}`;
    const translatedType = this.translate.instant(translationKey);
    return translatedType === translationKey ? type : translatedType;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object';
  }

  private clearDeactivationError(): void {
    this.deactivationErrorMessage = null;
    this.unsyncedItems = [];
  }

  private clearLocalState() {
    // 1. Reset all NgRx stores back to their initial states
    this.store.dispatch(resetCartStates());
    this.store.dispatch(resetCouponStates());
    this.store.dispatch(resetCustomerStates());
    this.store.dispatch(resetEmployeeStates());
    this.store.dispatch(resetGlobalRulesStates());
    this.store.dispatch(resetHeldOrderStates());
    this.store.dispatch(resetProductStates());
    this.store.dispatch(resetPaymentStates());
    this.store.dispatch(resetLoyaltyStates());

    // 2. Clear out in-memory user permissions
    this.permissionService.clearPermissions();

    // 3. Clear localStorage
    localStorage.clear();
  }
}
